import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  useCandidate,
  useCreateInterview,
  useUpdateInterview,
} from '@/hooks/use-candidates';
import { useEmployees } from '@/hooks/use-employees';
import { ACTIVE_EMPLOYEE_STATUSES } from '@/lib/employees';
import { cn } from '@/lib/utils';
import type {
  CandidateAssignment,
  CandidateInterview,
  CreateInterviewInput,
  InterviewType,
  UpdateInterviewInput,
} from '@/types/candidates';
import type { EmployeeListItem } from '@/types/employee';
import { extractErrorMessage } from '@/lib/errors';
import { startOfDay } from 'date-fns';
import { Check, ChevronsUpDown, Loader2, X, AlertTriangle } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { INTERVIEW_TYPE_LABEL } from '../lib/interview-labels';

const TYPE_OPTIONS: InterviewType[] = ['HR_CALL', 'TECH', 'FINAL', 'OTHER'];

interface CandidateInterviewSchedulerProps {
  candidateId: string;
  interview?: CandidateInterview | null;
  embedded?: boolean;
  onDone?: () => void;
}

interface AttendeeChip {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
}

/** Client-side validation messages, keyed by the field each one points at. */
interface FieldErrors {
  date?: string;
  time?: string;
  from?: string;
  to?: string;
}

/** The values every message below is derived from. */
interface FieldValues {
  flexible: boolean;
  fixedDate: Date | null;
  fixedTime: string;
  fromDate: Date | null;
  toDate: Date | null;
}

/**
 * The one message for an inverted window, shared by the live check below and the
 * submit path so the same fact is never worded two ways.
 */
const WINDOW_ORDER_MESSAGE = '"From" must be before or equal to "To"';

const TIME_PATTERN = /^\d{2}:\d{2}$/;

/**
 * Everything the form has to say about the values as they stand, in one pass.
 *
 * Submit and every later edit share it, so the two can never disagree about
 * what is wrong. The save button is gated on the messages on screen, which puts
 * a hard requirement on that: a set carried over from an earlier submit would
 * leave the button disabled while naming only some of the faults it is disabled
 * for — empty the "Time" field after a submit that flagged an empty "Date", and
 * only one of the two would be on screen.
 */
function collectFieldErrors(values: FieldValues): FieldErrors {
  const errors: FieldErrors = {};

  if (!values.flexible) {
    if (!values.fixedDate) errors.date = 'Pick a date';
    if (!TIME_PATTERN.test(values.fixedTime)) errors.time = 'Time must be HH:mm';
    return errors;
  }

  if (!values.fromDate) errors.from = 'Pick a date';
  if (!values.toDate) errors.to = 'Pick a date';
  // Replaces the "To" placeholder only when both ends exist — the order is a
  // fact about the pair, not about either end on its own.
  if (isWindowInverted(values.fromDate, values.toDate)) errors.to = WINDOW_ORDER_MESSAGE;
  return errors;
}

/**
 * Every field that can carry a message, in the order the fields are read, with
 * the id of the control it describes. Exhaustive by type, which is what makes
 * `FIELD_ORDER` below trustworthy; the ids themselves only reach the markup — a
 * blocked submit gets to the input through a ref, not through the DOM.
 */
const FIELD_INPUT_IDS: Record<keyof FieldErrors, string> = {
  date: 'interview-date',
  time: 'interview-time',
  from: 'interview-from',
  to: 'interview-to',
};

// Derived rather than written out a second time: as a `Record` the map above is
// exhaustive, so a field added to `FieldErrors` cannot reach this order list
// half-way and leave its message unwatched by `sameErrors` and unfocused by a
// blocked submit.
const FIELD_ORDER = Object.keys(FIELD_INPUT_IDS) as Array<keyof FieldErrors>;

function sameErrors(a: FieldErrors, b: FieldErrors): boolean {
  return FIELD_ORDER.every((field) => a[field] === b[field]);
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

/**
 * True when the window reads backwards.
 *
 * Each end is first moved to the same time of day on its own date — the noon
 * submit pins them to — so only the calendar days decide the answer, which is
 * the granularity a window actually has. Every value that reaches these fields
 * today is already local midnight, so a raw comparison would agree; the
 * normalisation keeps this predicate and the submit check in step even if some
 * caller later hands the form a Date carrying a time of day.
 */
function isWindowInverted(from: Date | null, to: Date | null): boolean {
  if (!from || !to) return false;
  return toLocalNoon(from).getTime() > toLocalNoon(to).getTime();
}

function toLocalNoon(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    12,
    0,
    0,
    0,
  );
}

export function CandidateInterviewScheduler({
  candidateId,
  interview,
  embedded = false,
  onDone,
}: CandidateInterviewSchedulerProps) {
  const isEdit = !!interview;
  const { data: candidate } = useCandidate(candidateId);
  const createInterview = useCreateInterview(candidateId);
  const updateInterview = useUpdateInterview(candidateId, { suppressGlobalToast: true });
  const isPending = isEdit ? updateInterview.isPending : createInterview.isPending;

  const [type, setType] = useState<InterviewType>(interview?.type ?? 'HR_CALL');
  const [assignmentId, setAssignmentId] = useState<string>(
    interview?.assignmentId ?? '__none',
  );
  const [flexible, setFlexible] = useState(!!interview?.availabilityWindow);

  const initialFixedDate = interview?.scheduledAt ? new Date(interview.scheduledAt) : null;
  const [fixedDate, setFixedDate] = useState<Date | null>(initialFixedDate);
  const [fixedTime, setFixedTime] = useState<string>(
    initialFixedDate
      ? `${pad2(initialFixedDate.getHours())}:${pad2(initialFixedDate.getMinutes())}`
      : '10:00',
  );

  // Normalised to local midnight, the basis every other value in these two
  // fields already has: a calendar pick and a parsed text entry both land on
  // midnight, while a stored window arrives pinned to noon (see the submit
  // path). Seeding an edit straight from the stored value would make the two
  // ends of the same window differ by twelve hours as soon as one of them was
  // re-picked — a difference nothing here should have to reason about. Submit
  // re-pins to noon, so the request payload is unchanged.
  const [fromDate, setFromDate] = useState<Date | null>(
    interview?.availabilityWindow ? startOfDay(new Date(interview.availabilityWindow.from)) : null,
  );
  const [toDate, setToDate] = useState<Date | null>(
    interview?.availabilityWindow ? startOfDay(new Date(interview.availabilityWindow.to)) : null,
  );

  const [meetingLink, setMeetingLink] = useState(interview?.meetingLink ?? '');
  const [attendees, setAttendees] = useState<AttendeeChip[]>(
    interview?.attendees ?? [],
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [apiError, setApiError] = useState<string | null>(null);

  // Raised by the first submit, lowered again by Reset and by the mode switch
  // that swaps the date fields out. While it holds, every edit recomputes the
  // whole message set, so the gated submit button can never be disabled for more
  // reasons than it is showing. A ref rather than state: nothing renders from
  // it, and the handlers that lower it have to read it back in the same event.
  const submittedRef = useRef(false);

  // Held so a blocked submit can put the caret straight on the field it names.
  const dateInputRef = useRef<HTMLInputElement>(null);
  const timeInputRef = useRef<HTMLInputElement>(null);
  const fromInputRef = useRef<HTMLInputElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);
  const fieldInputRefs: Record<keyof FieldErrors, React.RefObject<HTMLInputElement | null>> = {
    date: dateInputRef,
    time: timeInputRef,
    from: fromInputRef,
    to: toInputRef,
  };

  const activeAssignments = useMemo<CandidateAssignment[]>(
    () => candidate?.assignments.filter((a) => a.result === 'IN_PROGRESS') ?? [],
    [candidate],
  );

  const hasFieldErrors = Object.keys(errors).length > 0;

  /**
   * Recomputes what the form says, with `committed` standing in for the value
   * just handed to a setter — that state is not readable yet inside the handler
   * that set it.
   *
   * Until the first submit the form stays quiet about fields the user has not
   * filled in; that submit is what raises them. The one message that speaks up
   * on its own is the window's order message, because it names a conflict
   * between two dates the user did choose. It used to be "resolved" by deleting
   * "To" outright, so a deliberately picked date vanished with nothing naming
   * the loss. Editing "From" in place makes that worse than it sounds: the
   * picker commits every complete date the text passes through, and date-fns
   * reads an abbreviation as a month, so retyping the month of "July 15, 2026"
   * as September commits 15 September the moment "Sep" is complete — later than
   * a "To" in August, several keystrokes before the word is finished. Neither
   * end is deleted now; the pair is reported instead.
   *
   * After a submit the whole set is live, so an edit that uncovers a second
   * fault shows it immediately instead of waiting for the first to be fixed.
   */
  const syncMessages = (committed: Partial<FieldValues> = {}) => {
    const all = collectFieldErrors({
      flexible,
      fixedDate,
      fixedTime,
      fromDate,
      toDate,
      ...committed,
    });

    // The updater reads `submittedRef` rather than a captured value: every
    // handler that lowers the flag does so before enqueueing its own
    // `setErrors`, and React cannot flush between the two halves of one event,
    // so the branch below always matches the mode the caller meant.
    setErrors((prev) => {
      if (submittedRef.current) return sameErrors(prev, all) ? prev : all;
      if (all.to === WINDOW_ORDER_MESSAGE) {
        return prev.to === WINDOW_ORDER_MESSAGE
          ? prev
          : { ...prev, to: WINDOW_ORDER_MESSAGE };
      }
      // Retracts the order message alone. Before a submit it is the only
      // message that can be here at all — everything that lowers the flag
      // clears the set in the same batch — so this leaves any other key it
      // finds untouched rather than assuming there is none.
      if (prev.to !== WINDOW_ORDER_MESSAGE) return prev;
      const next = { ...prev };
      delete next.to;
      return next;
    });
  };

  const reset = () => {
    setType('HR_CALL');
    setAssignmentId('__none');
    setFlexible(false);
    setFixedDate(null);
    setFixedTime('10:00');
    setFromDate(null);
    setToDate(null);
    setMeetingLink('');
    setAttendees([]);
    setErrors({});
    setApiError(null);
    submittedRef.current = false;
  };

  const handleSubmit = async () => {
    setApiError(null);

    // Every message in one pass, so a form with two empty fields flags both
    // instead of revealing them one submit at a time.
    const nextErrors = collectFieldErrors({
      flexible,
      fixedDate,
      fixedTime,
      fromDate,
      toDate,
    });
    submittedRef.current = true;
    setErrors(nextErrors);

    const firstInvalid = FIELD_ORDER.find((field) => nextErrors[field]);
    if (firstInvalid) {
      // These messages are about to disable the button the user just pressed,
      // which would drop focus to the dialog container and restart tabbing from
      // its top. Put the caret on the field that has to change instead. Only a
      // mounted field can carry a message, so the ref is set.
      fieldInputRefs[firstInvalid].current?.focus();
      return;
    }

    let scheduledAt: string | undefined;
    let availabilityWindow: { from: string; to: string } | undefined;

    if (!flexible) {
      if (fixedDate) {
        const [hh, mm] = fixedTime.split(':').map(Number);
        const combined = new Date(fixedDate);
        combined.setHours(hh, mm, 0, 0);
        scheduledAt = combined.toISOString();
      }
    } else if (fromDate && toDate) {
      // DatePicker returns local-midnight Dates. toISOString() on midnight can
      // cross a day boundary under any non-zero UTC offset (−12h on Pacific,
      // +14h on Kiritimati), which the backend/reader then renders on the wrong
      // day. Pin to local noon so the instant sits in the middle of the
      // intended calendar day. The order was already judged above, on the same
      // calendar days these instants carry, so a single-day window — the day is
      // agreed, only the hour is still open — passes and only an inverted range
      // is stopped.
      availabilityWindow = {
        from: toLocalNoon(fromDate).toISOString(),
        to: toLocalNoon(toDate).toISOString(),
      };
    }

    try {
      if (isEdit && interview) {
        const input: UpdateInterviewInput = {
          type,
          meetingLink: meetingLink.trim() || undefined,
          attendeeIds: attendees.map((a) => a.id),
        };
        if (scheduledAt !== undefined) input.scheduledAt = scheduledAt;
        if (availabilityWindow !== undefined) input.availabilityWindow = availabilityWindow;
        await updateInterview.mutateAsync({ interviewId: interview.id, input });
        onDone?.();
      } else {
        const input: CreateInterviewInput = {
          type,
          attendeeIds: attendees.map((a) => a.id),
        };
        if (assignmentId !== '__none') input.assignmentId = assignmentId;
        if (meetingLink.trim()) input.meetingLink = meetingLink.trim();
        if (scheduledAt !== undefined) input.scheduledAt = scheduledAt;
        if (availabilityWindow !== undefined) input.availabilityWindow = availabilityWindow;
        await createInterview.mutateAsync(input);
        reset();
        onDone?.();
      }
    } catch (err: unknown) {
      setApiError(
        extractErrorMessage(
          err,
          isEdit ? 'Failed to update interview' : 'Failed to create interview',
        ),
      );
    }
  };

  return (
    <div
      className={cn(
        'space-y-4',
        !embedded && 'rounded-lg border border-border bg-background p-5',
      )}
    >
      {!embedded && (
        <div>
          <h2 className="text-base font-medium">Schedule interview</h2>
          <p className="text-sm text-muted-foreground">
            Pick a time or a window. Attendees and the candidate will be emailed
            (calendar invite included for fixed times).
          </p>
        </div>
      )}

      {!candidate?.email && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            The candidate has no email on file — they won't get an invite.
            Attendees will still be notified.
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Type">
          <Select value={type} onValueChange={(v) => setType(v as InterviewType)}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((t) => (
                <SelectItem key={t} value={t}>
                  {INTERVIEW_TYPE_LABEL[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Vacancy">
          <Select
            value={assignmentId}
            onValueChange={setAssignmentId}
            disabled={isEdit}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="— not linked —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">— not linked —</SelectItem>
              {activeAssignments.map((a) => (
                <SelectItem
                  key={a.id}
                  value={a.id}
                  className="[&>span:last-child]:truncate"
                  title={a.vacancyTitle}
                >
                  {a.vacancyTitle}
                </SelectItem>
              ))}
              {isEdit && interview?.assignment &&
                !activeAssignments.some((a) => a.id === interview.assignment?.id) ? (
                <SelectItem
                  value={interview.assignment.id}
                  className="[&>span:last-child]:truncate"
                  title={interview.assignment.vacancyTitle}
                >
                  {interview.assignment.vacancyTitle}
                </SelectItem>
              ) : null}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="flex-toggle"
          checked={flexible}
          onCheckedChange={(next) => {
            setFlexible(next);
            // The toggle swaps which date fields are mounted, so any pending
            // message would point at a field that is no longer on screen — and
            // the messages a submit raised about those fields no longer stand,
            // so the set starts over and the next submit fills it again.
            submittedRef.current = false;
            setErrors((prev) => (Object.keys(prev).length ? {} : prev));
            // Switching away and back keeps the window's dates, so the order
            // message has to come back with them — otherwise the round trip
            // would present an inverted window as a valid one.
            if (next) syncMessages({ flexible: next });
          }}
        />
        <label htmlFor="flex-toggle" className="text-sm text-foreground cursor-pointer">
          Flexible time window (candidate picks later)
        </label>
      </div>

      {!flexible ? (
        // Keyed so the switch unmounts this row instead of letting React reuse
        // its pickers for the window's: a `DatePicker` keeps the text being
        // typed in its own state and deliberately stops re-syncing it from
        // `value` while it holds focus, so a reused instance would carry the
        // fixed date's text into "From" while the form holds nothing there.
        // Reserved lane for the out-of-flow field messages (see `Field`), deep
        // enough for the two lines a message wraps to once the dialog goes
        // full-width below the `sm` breakpoint.
        <div key="fixed-time" className="grid grid-cols-2 gap-3 pb-10">
          <Field label="Date" error={errors.date} errorId="interview-date-error">
            <DatePicker
              id={FIELD_INPUT_IDS.date}
              inputRef={dateInputRef}
              value={fixedDate}
              onChange={(d) => {
                setFixedDate(d);
                syncMessages({ fixedDate: d });
              }}
              invalid={!!errors.date}
              errorId="interview-date-error"
            />
          </Field>
          <Field label="Time" error={errors.time} errorId="interview-time-error">
            {/* Safari: `<input type="time" step="60">` → HH:mm. */}
            <Input
              id={FIELD_INPUT_IDS.time}
              ref={timeInputRef}
              type="time"
              step={60}
              value={fixedTime}
              onChange={(e) => {
                setFixedTime(e.target.value);
                syncMessages({ fixedTime: e.target.value });
              }}
              aria-invalid={!!errors.time}
              aria-describedby={errors.time ? 'interview-time-error' : undefined}
              className={cn(
                'h-9 dark:[color-scheme:dark]',
                errors.time && 'border-destructive focus-visible:ring-destructive',
              )}
            />
          </Field>
        </div>
      ) : (
        // Keyed and laned exactly as the fixed-time row above.
        <div key="window" className="grid grid-cols-2 gap-3 pb-10">
          <Field label="From" error={errors.from} errorId="interview-from-error">
            <DatePicker
              id={FIELD_INPUT_IDS.from}
              inputRef={fromInputRef}
              value={fromDate}
              onChange={(d) => {
                setFromDate(d);
                syncMessages({ fromDate: d });
              }}
              // Neither end constrains the other — an out-of-order pair is
              // reported, not refused — so each opens on the other's month
              // rather than on today. On "To" that restores what its `minDate`
              // used to do as a side effect; on "From", which never had one,
              // it is new, and it keeps the pair symmetrical whichever end the
              // user fills in first.
              anchorMonth={toDate}
              invalid={!!errors.from}
              errorId="interview-from-error"
            />
          </Field>
          <Field label="To" error={errors.to} errorId="interview-to-error">
            <DatePicker
              id={FIELD_INPUT_IDS.to}
              inputRef={toInputRef}
              value={toDate}
              onChange={(d) => {
                setToDate(d);
                syncMessages({ toDate: d });
              }}
              anchorMonth={fromDate}
              invalid={!!errors.to}
              errorId="interview-to-error"
            />
          </Field>
        </div>
      )}

      <Field label="Meeting link (optional)">
        <Input
          value={meetingLink}
          onChange={(e) => setMeetingLink(e.target.value)}
          placeholder="https://meet.google.com/…"
          className="h-9"
        />
      </Field>

      <Field label="Attendees">
        <AttendeePicker value={attendees} onChange={setAttendees} />
      </Field>

      {/* Request failures only — field validation renders under its own field. */}
      {apiError ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2.5 text-sm text-destructive">
          {apiError}
        </div>
      ) : null}

      <div className="flex items-center justify-end gap-2 pt-1">
        {isEdit ? (
          <Button type="button" variant="outline" onClick={onDone} disabled={isPending}>
            Cancel
          </Button>
        ) : (
          <Button type="button" variant="outline" onClick={reset} disabled={isPending}>
            Reset
          </Button>
        )}
        {/* Gated on messages that are already on screen, never on untouched
            fields: an empty form still submits, and that submit is what raises
            the messages in the first place. Without this the button looks inert
            when a message is already showing — the click re-renders the same
            text in the same place and nothing moves. */}
        <Button
          type="button"
          variant="accent"
          onClick={handleSubmit}
          disabled={isPending || hasFieldErrors}
        >
          {isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
          {isEdit ? 'Save changes' : 'Schedule'}
        </Button>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  errorId,
  children,
}: {
  label: string;
  error?: string;
  errorId?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative flex flex-col gap-1">
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {children}
      </label>
      {/* Sibling of the label, not a child: inside it the message would be
          folded into the control's accessible name instead of describing it.
          Out of flow, so nothing below moves when a message appears or
          retracts — the date pickers commit mid-word, so the order message can
          flicker between two keystrokes, and in flow that walked the rest of
          the form and the footer buttons up and down under the cursor. The lane
          it renders into is reserved by the row that owns the field — the two
          date rows above carry `pb-10` for exactly this; a `Field` given an
          `error` outside such a row would overlap whatever follows it. */}
      {error ? (
        <p
          id={errorId}
          className="absolute top-full left-0 mt-1 text-xs text-destructive"
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

interface AttendeePickerProps {
  value: AttendeeChip[];
  onChange: (next: AttendeeChip[]) => void;
}

function AttendeePicker({ value, onChange }: AttendeePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search), 200);
    return () => window.clearTimeout(t);
  }, [search]);

  const { data } = useEmployees(
    debounced
      ? { search: debounced, limit: 10, statuses: ACTIVE_EMPLOYEE_STATUSES }
      : { limit: 10, statuses: ACTIVE_EMPLOYEE_STATUSES },
  );
  const selectedIds = new Set(value.map((a) => a.id));
  const suggestions: EmployeeListItem[] = data?.data ?? [];

  const togglePick = (emp: EmployeeListItem) => {
    if (selectedIds.has(emp.id)) {
      onChange(value.filter((a) => a.id !== emp.id));
    } else {
      onChange([
        ...value,
        {
          id: emp.id,
          firstName: emp.firstName,
          lastName: emp.lastName,
          avatarUrl: emp.avatarUrl ?? null,
        },
      ]);
    }
  };

  const remove = (id: string) => {
    onChange(value.filter((a) => a.id !== id));
  };

  const triggerLabel =
    value.length === 0
      ? 'Search by name…'
      : value.length === 1
        ? `${value[0].firstName} ${value[0].lastName}`
        : `${value.length} attendees`;

  return (
    <div className="space-y-2">
      {/* `modal`: this scheduler lives inside a modal Dialog, whose scroll lock would otherwise
          swallow the wheel over the portaled attendee list (same fix as HW-677). */}
      <Popover modal open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'h-9 w-full justify-between font-normal',
              value.length === 0 && 'text-muted-foreground',
            )}
          >
            <span className="truncate" title={value.length > 0 ? triggerLabel : undefined}>
              {triggerLabel}
            </span>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              value={search}
              onValueChange={setSearch}
              placeholder="Search by name…"
            />
            <CommandList>
              <CommandEmpty>No people found.</CommandEmpty>
              <CommandGroup>
                {suggestions.map((emp) => {
                  const selected = selectedIds.has(emp.id);
                  return (
                    <CommandItem
                      key={emp.id}
                      value={`${emp.firstName} ${emp.lastName} ${emp.id}`}
                      onSelect={() => togglePick(emp)}
                    >
                      {emp.avatarUrl ? (
                        <img
                          src={emp.avatarUrl}
                          alt=""
                          className="h-5 w-5 shrink-0 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
                          {emp.firstName[0] ?? ''}
                          {emp.lastName[0] ?? ''}
                        </div>
                      )}
                      <span className="truncate" title={`${emp.firstName} ${emp.lastName}`}>
                        {emp.firstName} {emp.lastName}
                      </span>
                      <Check
                        className={cn(
                          'ml-auto h-3.5 w-3.5 shrink-0',
                          selected ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {value.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {value.map((a) => (
            <span
              key={a.id}
              className="inline-flex max-w-[220px] items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs"
              title={`${a.firstName} ${a.lastName}`}
            >
              {a.avatarUrl ? (
                <img
                  src={a.avatarUrl}
                  alt=""
                  className="h-4 w-4 rounded-full object-cover"
                />
              ) : null}
              <span className="truncate">
                {a.firstName} {a.lastName}
              </span>
              <button
                type="button"
                onClick={() => remove(a.id)}
                className="ml-0.5 text-muted-foreground hover:text-destructive"
                aria-label={`Remove ${a.firstName}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
